import React from "react";
import PropTypes from "prop-types";
import { Clock } from "react-feather";
import { Cols, Field, Section, SwitchField, inputStyle, warnStyle, hintStyle } from "../common/FormKit";

/**
 * Points expiry settings of a percentage programme, shared by the add and edit loyalty pages.
 *
 * Bounds mirror the API (src/const/loyalty.js POINTS_EXPIRY_LIMITS). Change both together.
 */
export const POINTS_EXPIRY_LIMITS = {
    minValidityMonths: 1,
    maxValidityMonths: 60,
    minReminderDays: 1,
    maxReminderDays: 365,
    maxExpiryReminders: 5,
};

const DEFAULT_LADDER = [60, 30, 7];

/** Form state from an API programme row (or the defaults for a new one). Inputs hold strings. */
export const pointsExpiryFormFrom = (data = {}) => {
    const ladder = Array.isArray(data.expiryReminderDays) ? data.expiryReminderDays : DEFAULT_LADDER;
    return {
        pointsExpiryEnabled: !!data.pointsExpiryEnabled,
        pointsValidityMonths: String(data.pointsValidityMonths ?? 12),
        // null on the row means "off": an empty input.
        inactivityReminderDays: data.inactivityReminderDays === null ? "" : String(data.inactivityReminderDays ?? 30),
        expiryReminderDays: ladder.map(String),
    };
};

const toInt = (v) => (String(v).trim() === "" ? null : Number(v));

const ladderOf = (form) =>
    form.expiryReminderDays.map(toInt).filter((d) => d !== null);

/** The payload keys for the API. */
export const pointsExpiryPayload = (form) => ({
    pointsExpiryEnabled: !!form.pointsExpiryEnabled,
    pointsValidityMonths: toInt(form.pointsValidityMonths),
    inactivityReminderDays: toInt(form.inactivityReminderDays),
    expiryReminderDays: [...new Set(ladderOf(form))].sort((a, b) => b - a),
});

/** First problem as a message, or null. Same rules as the API, so a save is not refused there. */
export const validatePointsExpiryForm = (form) => {
    const L = POINTS_EXPIRY_LIMITS;
    const months = toInt(form.pointsValidityMonths);
    if (!Number.isInteger(months) || months < L.minValidityMonths || months > L.maxValidityMonths) {
        return `Points validity must be a whole number of months from ${L.minValidityMonths} to ${L.maxValidityMonths}.`;
    }

    const inactivity = toInt(form.inactivityReminderDays);
    if (inactivity !== null && (!Number.isInteger(inactivity) || inactivity < L.minReminderDays || inactivity > L.maxReminderDays)) {
        return `Inactivity reminder must be from ${L.minReminderDays} to ${L.maxReminderDays} days, or empty to turn it off.`;
    }

    const ladder = ladderOf(form);
    if (ladder.length > L.maxExpiryReminders) {
        return `At most ${L.maxExpiryReminders} expiry reminders.`;
    }
    for (const d of ladder) {
        if (!Number.isInteger(d) || d < L.minReminderDays || d > L.maxReminderDays) {
            return `Each expiry reminder must be a whole number of days from ${L.minReminderDays} to ${L.maxReminderDays}.`;
        }
    }
    if (new Set(ladder).size !== ladder.length) {
        return "Expiry reminders must not repeat a day.";
    }
    const tooLate = ladder.find((d) => d >= months * 28);
    if (tooLate !== undefined) {
        return `An expiry reminder of ${tooLate} days does not fit in a validity of ${months} month(s).`;
    }
    return null;
};

const PointsExpirySection = ({ value, onChange, wasEnabled = false }) => {
    const set = (patch) => onChange({ ...value, ...patch });
    const setStep = (index, v) =>
        set({ expiryReminderDays: value.expiryReminderDays.map((d, i) => (i === index ? v : d)) });
    const removeStep = (index) =>
        set({ expiryReminderDays: value.expiryReminderDays.filter((_, i) => i !== index) });
    const addStep = () => set({ expiryReminderDays: [...value.expiryReminderDays, ""] });

    const ladder = ladderOf(value).filter(Number.isInteger);
    const floorDays = (ladder.length ? Math.max(...ladder) : 0) + 1;
    const turningOn = value.pointsExpiryEnabled && !wasEnabled;

    return (
        <Section
            icon={Clock}
            title="Points expiry"
            subtitle="Unused points expire after a period without a qualifying purchase. A new purchase renews the whole balance."
        >
            <SwitchField
                label="Expire unused points"
                checked={value.pointsExpiryEnabled}
                onChange={(checked) => set({ pointsExpiryEnabled: checked })}
                hint="Only the spendable balance expires. Lifetime points, and so the customer's tier, are kept."
            />

            {value.pointsExpiryEnabled && (
                <>
                    <Cols min={200}>
                        <Field label="Valid for (months)" required hint="Counted from the last qualifying purchase.">
                            <input
                                type="number"
                                min={POINTS_EXPIRY_LIMITS.minValidityMonths}
                                max={POINTS_EXPIRY_LIMITS.maxValidityMonths}
                                aria-label="Valid for (months)"
                                style={inputStyle}
                                value={value.pointsValidityMonths}
                                onChange={(e) => set({ pointsValidityMonths: e.target.value })}
                            />
                        </Field>
                        <Field label="Inactivity reminder after (days)" hint="One push per purchase. Leave empty to turn it off.">
                            <input
                                type="number"
                                min={POINTS_EXPIRY_LIMITS.minReminderDays}
                                max={POINTS_EXPIRY_LIMITS.maxReminderDays}
                                aria-label="Inactivity reminder after (days)"
                                placeholder="Off"
                                style={inputStyle}
                                value={value.inactivityReminderDays}
                                onChange={(e) => set({ inactivityReminderDays: e.target.value })}
                            />
                        </Field>
                    </Cols>

                    <Field
                        label="Expiry reminders (days before)"
                        hint="A push and a Wallet card update on each of these days. A customer who is already inside a window gets only the nearest one."
                    >
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                            {value.expiryReminderDays.map((d, i) => (
                                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <input
                                        type="number"
                                        min={POINTS_EXPIRY_LIMITS.minReminderDays}
                                        aria-label={`Expiry reminder ${i + 1}`}
                                        style={{ ...inputStyle, width: 84 }}
                                        value={d}
                                        onChange={(e) => setStep(i, e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        aria-label={`Remove expiry reminder ${i + 1}`}
                                        onClick={() => removeStep(i)}
                                        style={{ border: "none", background: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16, padding: "0 4px" }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {value.expiryReminderDays.length < POINTS_EXPIRY_LIMITS.maxExpiryReminders && (
                                <button
                                    type="button"
                                    onClick={addStep}
                                    style={{ border: "1px dashed #cbd5e1", background: "none", borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer" }}
                                >
                                    + Add reminder
                                </button>
                            )}
                        </div>
                    </Field>

                    {turningOn ? (
                        <span style={warnStyle} role="note">
                            On save, every current balance gets an expiry date. Customers whose last purchase is older than the
                            validity get {floorDays} days, so every reminder is sent before anything expires.
                        </span>
                    ) : (
                        <span style={hintStyle}>
                            Changing the validity re-dates every balance, never closer than {floorDays} days from the save.
                        </span>
                    )}
                </>
            )}
        </Section>
    );
};

PointsExpirySection.propTypes = {
    value: PropTypes.shape({
        pointsExpiryEnabled: PropTypes.bool,
        pointsValidityMonths: PropTypes.string,
        inactivityReminderDays: PropTypes.string,
        expiryReminderDays: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    }).isRequired,
    onChange: PropTypes.func.isRequired,
    wasEnabled: PropTypes.bool,
};

export default PointsExpirySection;
